import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, tap, throwError } from 'rxjs';
import { OptionDto } from '@bioreferencehealth/ui-kit';
import { ReportingProvidersStateModel } from './models/reporting-providers-state.model';
import { ReportingProviderDto } from '../models/reporting-provider-dto';
import { ReportingProviderApiService } from '../services/reporting-provider-api.service';
import { CurrentAccountState } from 'src/states';
import { PracticeDto } from 'src/shared/models/practice-dto';
import { FetchReportingProviders } from 'src/app/states/reporting-providers.actions';
import { CurrentAccountRestored, CurrentPracticeUpdated } from 'src/states/current-account/current-account.actions';
import { Logout } from 'src/modules/security/actions/auth-actions';
import { LoadStatus } from 'src/app/enums/load-status.enum';
import { startOfDay } from 'date-fns';
import { DEFAULT_MAX_RESULT_DAYS } from 'src/app/pages/order-results/const/default-max-result-days.const';

export const REPORTING_PROVIDERS_STATE_NAME = 'reportingProviders';
const defaults: ReportingProvidersStateModel = {
  status: LoadStatus.NotLoaded,
  loadedStartOfDayTimestamp: 0,
  providers: []
};

@State<ReportingProvidersStateModel>({
  name: REPORTING_PROVIDERS_STATE_NAME,
  defaults
})
@Injectable()
export class ReportingProvidersState {
  private readonly store = inject(Store);
  private readonly api = inject(ReportingProviderApiService);

  @Selector()
  public static loaded(state: ReportingProvidersStateModel): boolean {
    return state.status === LoadStatus.Loaded;
  }

  @Selector()
  public static loadedStartOfDayTimestamp(state: ReportingProvidersStateModel): number {
    return state.loadedStartOfDayTimestamp;
  }

  @Selector()
  public static providers(state: ReportingProvidersStateModel): OptionDto<ReportingProviderDto>[] {
    return state.providers ?? [];
  }

  @Action(FetchReportingProviders)
  public fetchReportingProviders(ctx: StateContext<ReportingProvidersStateModel>): Observable<OptionDto<ReportingProviderDto>[]> {
    const userPractice = this.store.selectSnapshot(CurrentAccountState.userPractice);

    if (userPractice) {
      ctx.patchState({ status: LoadStatus.Loading });
      return this.fetchProviders(userPractice, DEFAULT_MAX_RESULT_DAYS).pipe(
        tap((providers: OptionDto<ReportingProviderDto>[]) =>
          ctx.patchState({
            providers,
            loadedStartOfDayTimestamp: startOfDay(new Date()).getTime(),
            status: LoadStatus.Loaded
          })
        ),
        catchError(error => {
          ctx.patchState({ status: LoadStatus.Error });
          return throwError(() => error);
        })
      );
    }

    return EMPTY;
  }

  @Action([CurrentAccountRestored, CurrentPracticeUpdated, Logout])
  public clearState(ctx: StateContext<ReportingProvidersStateModel>): void {
    ctx.setState(defaults);
  }

  private fetchProviders(practice: PracticeDto, maxResultDays: number): Observable<OptionDto<ReportingProviderDto>[]> {
    return this.api.getReportingProviders(practice.id, { maxResultDays }).pipe(
      map((providers: ReportingProviderDto[]) => {
        return providers.map(provider => {
          return {
            id: provider.id,
            value: provider.fullName,
            payload: provider
          } as OptionDto<ReportingProviderDto>;
        });
      })
    );
  }
}
